import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowDown, ArrowLeft, ArrowUp, Eye, History, Layers, Plus, RefreshCw, Save, Send, Trash2, Undo2 } from 'lucide-react';
import api from '../../../config/axios';
import { API, ROUTES } from '../../../config/links';
import { dynamicFieldsApi } from './api';
import { DraftEntry, blankEntry, toDraft, toPayload, validateDraft } from './draft';
import EntryEditor from './EntryEditor';
import ImpactDialog from './ImpactDialog';
import { libraryImpact } from './impact';
import { ENTRY_KIND_LABELS, LibraryDetail, LibraryEntryKind, LibraryImpact } from './types';
import { useLibraryRef } from './useLibraryRef';
import PublishDialog from '../editor/PublishDialog';
import { Chip } from '../components/history';
import { TourButton } from '../tour/TourButton';
import { dangerIconButton, iconButton, inputClass, primaryButton, secondaryButton, Skeleton } from '../ui';

const errorText = (e: any, fallback: string) => {
  const detail = e?.response?.data?.detail;
  return (typeof detail === 'string' ? detail : detail?.message) || e?.message || fallback;
};

/** Define the entries of one (sensor type, category) library: draft, publish, then forms select from it. */
export default function DynamicFieldsEditorPage() {
  const ref = useLibraryRef();
  const navigate = useNavigate();
  const [current, setCurrent] = useState<LibraryDetail | null>();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<DraftEntry[]>([]);
  const [baseline, setBaseline] = useState('[]');
  const [selectedUid, setSelectedUid] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [note, setNote] = useState('');
  const [impact, setImpact] = useState<LibraryImpact>();

  const serialize = (draft: DraftEntry[]) => JSON.stringify(toPayload(draft));
  const dirty = useMemo(() => serialize(entries) !== baseline, [entries, baseline]);
  const problems = useMemo(() => validateDraft(entries), [entries]);
  const hasProblems = Object.keys(problems).length > 0;

  const apply = useCallback((detail: LibraryDetail | null) => {
    const draft = toDraft(detail?.fields || []);
    setCurrent(detail);
    setEntries(draft);
    setBaseline(JSON.stringify(toPayload(draft)));
    setSelectedUid((prev) => (prev && draft.some((e) => e._uid === prev) ? prev : draft[0]?._uid));
  }, []);

  const load = useCallback(() => {
    if (!ref) return;
    setLoading(true);
    dynamicFieldsApi.current(ref)
      .then(apply)
      .catch((e) => toast.error(errorText(e, 'Failed to load the library')))
      .finally(() => setLoading(false));
  }, [ref, apply]);
  useEffect(load, [load]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  if (!ref) {
    return <div className="grid min-h-full place-items-center bg-slate-50 p-8 text-sm text-slate-500">Choose a library from the <button className="ml-1 font-medium text-primary-700 underline" onClick={() => navigate(ROUTES.DYNAMIC_FIELDS)}>dynamic fields list</button>.</div>;
  }

  const selected = entries.find((e) => e._uid === selectedUid);
  const update = (next: DraftEntry) => setEntries((list) => list.map((e) => (e._uid === next._uid ? next : e)));
  const add = (kind: LibraryEntryKind) => {
    const entry = blankEntry(kind);
    setEntries((list) => [...list, entry]);
    setSelectedUid(entry._uid);
  };
  const move = (uid: string, delta: -1 | 1) => setEntries((list) => {
    const index = list.findIndex((e) => e._uid === uid);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= list.length) return list;
    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });
  const remove = (uid: string) => {
    setEntries((list) => list.filter((e) => e._uid !== uid));
    setSelectedUid((prev) => (prev === uid ? undefined : prev));
  };

  const save = async (): Promise<boolean> => {
    if (hasProblems) { toast.error('Fix the highlighted entries first'); return false; }
    setBusy(true);
    try {
      apply(await dynamicFieldsApi.saveDraft(ref, toPayload(entries)));
      toast.success('Draft saved');
      return true;
    } catch (e) {
      toast.error(errorText(e, 'Save failed'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const publish = async (acknowledgeImpact = false) => {
    setPublishing(false);
    if (dirty && !(await save())) return;
    setBusy(true);
    try {
      const published = await dynamicFieldsApi.publish(ref, note, acknowledgeImpact);
      toast.success(`Version ${published.version} is live`);
      setNote('');
      setImpact(undefined);
      load();
    } catch (e) {
      // Removing entries that published forms select needs an explicit go-ahead.
      const found = libraryImpact(e);
      if (found) setImpact(found);
      else { setImpact(undefined); toast.error(errorText(e, 'Publish failed')); }
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (!window.confirm('Discard the draft? The published version is not affected.')) return;
    setBusy(true);
    try {
      await dynamicFieldsApi.discardDraft(ref);
      toast.success('Draft discarded');
      load();
    } catch (e) {
      toast.error(errorText(e, 'Could not discard the draft'));
    } finally {
      setBusy(false);
    }
  };

  const isDraft = current?.status === 'draft';
  const canPublish = (isDraft || dirty) && !hasProblems && entries.length > 0;

  return (
    <div className="flex min-h-full flex-col bg-slate-50 p-3 md:p-8 lg:h-full lg:min-h-0 lg:overflow-hidden">
      <header className="mb-4 flex shrink-0 flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-end sm:justify-between" data-tour="dynamic-fields-editor-header">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-600"><Layers className="h-3.5 w-3.5" /> Dynamic field library</div>
          <h1 className="mt-1 flex flex-wrap items-center gap-2 text-xl font-semibold tracking-tight text-slate-900">
            {ref.sensor_type} <span className="text-slate-300">/</span> {ref.category}
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {loading ? <Skeleton className="h-5 w-24" /> : (
              <>
                {current?.status === 'published' && <Chip cls="bg-emerald-100 text-emerald-700">Live v{current.version}</Chip>}
                {isDraft && <Chip cls="bg-amber-100 text-amber-700">Draft v{current!.version} (unpublished)</Chip>}
                {!current && <Chip cls="bg-slate-100 text-slate-500">New library</Chip>}
                {dirty && <Chip cls="bg-sky-100 text-sky-700">Unsaved changes</Chip>}
              </>
            )}
          </div>
          <p className="mt-1.5 max-w-2xl text-xs text-slate-500">Forms for this sensor type and category pick from the <strong>published</strong> entries. Saving only creates a draft; users keep seeing the live version until you publish.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <TourButton id="dynamicEditor" ready={!loading} />
          <button className={secondaryButton} onClick={load} disabled={loading || busy} title="Reload from the server"><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload</button>
          <button className={secondaryButton} onClick={() => navigate(ROUTES.dynamicFieldsVersions(ref.sensor_type, ref.category))}><Layers className="h-3.5 w-3.5" /> Versions</button>
          <button className={secondaryButton} onClick={() => navigate(ROUTES.dynamicFieldsAudit(ref.sensor_type, ref.category))}><History className="h-3.5 w-3.5" /> Audit log</button>
          <button className={secondaryButton} onClick={() => navigate(ROUTES.DYNAMIC_FIELDS)}><ArrowLeft className="h-3.5 w-3.5" /> All libraries</button>
          {isDraft && <button className={secondaryButton} onClick={discard} disabled={busy}><Undo2 className="h-3.5 w-3.5" /> Discard draft</button>}
          <button className={secondaryButton} data-tour="dynamic-fields-save" onClick={save} disabled={busy || !dirty || hasProblems}><Save className="h-3.5 w-3.5" /> Save draft</button>
          <button className={primaryButton} data-tour="dynamic-fields-publish" onClick={() => setPublishing(true)} disabled={busy || !canPublish}><Send className="h-3.5 w-3.5" /> Publish</button>
        </div>
      </header>

      <div className="grid gap-4 lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)]">
        <aside className="flex min-h-0 flex-col rounded-xl border border-slate-200 bg-white shadow-sm" aria-label="Entries">
          <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-slate-100 p-3" data-tour="dynamic-fields-add">
            <span className="mr-auto text-[13px] font-semibold text-slate-800">Entries <span className="font-normal text-slate-400">({entries.length})</span></span>
            <button className={secondaryButton} onClick={() => add('field')}><Plus className="h-3.5 w-3.5" /> Field</button>
            <button className={secondaryButton} onClick={() => add('temperature')}><Plus className="h-3.5 w-3.5" /> Temperature</button>
            <button className={secondaryButton} onClick={() => add('points')} disabled={entries.some((e) => e.kind === 'points')} title={entries.some((e) => e.kind === 'points') ? 'A library has one calibration points group' : undefined}><Plus className="h-3.5 w-3.5" /> Points</button>
          </div>
          <ol className="min-h-0 flex-1 space-y-1.5 overflow-y-auto p-2" data-tour="dynamic-fields-entries">
            {loading && Array.from({ length: 5 }).map((_, i) => <li key={i}><Skeleton className="h-12 w-full" /></li>)}
            {!loading && entries.length === 0 && <li className="p-6 text-center text-[12px] text-slate-400">No entries yet. Add the fields a form can select, for example BOL, AOL, temperature rows or the calibration points.</li>}
            {entries.map((entry, index) => {
              const bad = !!problems[entry._uid];
              return (
                <li key={entry._uid}>
                  <div className={`flex items-center gap-1 rounded-lg border p-2 ${entry._uid === selectedUid ? 'border-primary-400 bg-primary-50/40 ring-1 ring-primary-100' : bad ? 'border-red-300 bg-red-50/40' : 'border-slate-200 bg-white'}`}>
                    <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setSelectedUid(entry._uid)}>
                      <div className="flex items-center gap-1.5">
                        <span className={`truncate text-[12px] font-semibold ${entry.is_active ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{entry.label || 'Untitled'}</span>
                        {bad && <span className="text-[10px] font-semibold text-red-600">needs attention</span>}
                      </div>
                      <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                        <code className="truncate">{entry.key || '—'}</code>
                        <span>· {ENTRY_KIND_LABELS[entry.kind]}</span>
                      </div>
                    </button>
                    <button type="button" className={iconButton} aria-label="Move up" disabled={index === 0} onClick={() => move(entry._uid, -1)}><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button type="button" className={iconButton} aria-label="Move down" disabled={index === entries.length - 1} onClick={() => move(entry._uid, 1)}><ArrowDown className="h-3.5 w-3.5" /></button>
                    <button type="button" className={dangerIconButton} aria-label="Remove entry" onClick={() => remove(entry._uid)}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </li>
              );
            })}
          </ol>
        </aside>

        <section className="grid min-h-0 gap-4 overflow-y-auto xl:grid-cols-[minmax(0,1fr)_300px] xl:items-start" aria-label="Entry details">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-tour="dynamic-fields-entry-editor">
            {selected ? <EntryEditor key={selected._uid} entry={selected} problems={problems[selected._uid] || []} onChange={update} /> : <p className="py-10 text-center text-[12px] text-slate-400">Select an entry to edit it.</p>}
          </div>
          <PublishedPreview sensorType={ref.sensor_type} category={ref.category} available={current?.status === 'published' && !dirty} />
        </section>
      </div>

      {impact && (
        <ImpactDialog impact={impact} confirmLabel="Publish anyway" busy={busy} onConfirm={() => publish(true)} onCancel={() => setImpact(undefined)} />
      )}

      {publishing && (
        <PublishDialog
          note={note}
          onNoteChange={setNote}
          onClose={() => setPublishing(false)}
          onPublish={() => publish()}
          title="Publish these dynamic fields?"
          description="The draft becomes the live library straight away. Published forms that select its entries will use the new definitions. Earlier versions stay in history so you can roll back."
          placeholder="e.g. Added highTempF to the temperature rows"
        />
      )}
    </div>
  );
}

/** Calls the same endpoint a running form uses, so the preview shows what users really get. */
function PublishedPreview({ sensorType, category, available }: { sensorType: string; category: string; available: boolean }) {
  const [range, setRange] = useState('50mm');
  const [keys, setKeys] = useState<{ key: string; label: string }[]>();
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.get<{ fields: { key: string; label: string }[] }>(API.calibrationForms.calibrationDataSchema, { params: { sensor_type: sensorType, category, range } });
      setKeys(data.fields);
    } catch (e) {
      setKeys(undefined);
      toast.error(errorText(e, 'Preview failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm xl:sticky xl:top-0" aria-label="Live preview" data-tour="dynamic-fields-preview">
      <h3 className="text-[13px] font-semibold text-slate-800">Preview of the live library</h3>
      <p className="mt-0.5 text-[11px] text-slate-500">The fields a form receives at runtime. The range decides the calibration point keys.</p>
      <div className="mt-3 flex items-end gap-2">
        <label className="min-w-0 flex-1 text-[11px] text-slate-600">Range
          <input className={`${inputClass()} !mt-0.5 h-8 w-full`} value={range} onChange={(e) => setRange(e.target.value)} placeholder="e.g. 50mm" disabled={!available} />
        </label>
        <button className={secondaryButton} onClick={run} disabled={busy || !available}><Eye className="h-3.5 w-3.5" /> {busy ? 'Loading…' : 'Preview'}</button>
      </div>
      {!available && <p className="mt-3 text-[11px] text-amber-600">Save and publish your changes to preview the live library.</p>}
      {available && keys && (
        <div className="mt-3 flex max-h-[50vh] flex-wrap gap-1.5 overflow-y-auto">
          {keys.length === 0 ? <span className="text-[11px] text-slate-400">The live library returns no fields.</span> : keys.map((f) => (
            <span key={f.key} className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-700" title={f.label}><code>{f.key}</code></span>
          ))}
        </div>
      )}
    </aside>
  );
}
